import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Check } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function ClientHabits() {
  const [habits, setHabits] = useState([]);
  const [checkins, setCheckins] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", frequency: "daily", target_count: 1 });

  const load = async () => {
    const [h, c] = await Promise.all([
      api.get("/habits"),
      api.get("/habits/checkins", { params: { date: today() } }),
    ]);
    setHabits(h.data);
    const map = {};
    c.data.forEach((x) => (map[x.habit_id] = x.completed));
    setCheckins(map);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const toggle = async (habit_id, val) => {
    setCheckins({ ...checkins, [habit_id]: val });
    try {
      await api.post("/habits/checkins", { habit_id, date: today(), completed: val });
    } catch { toast.error("Could not save."); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/habits", form);
      toast.success("Habit added");
      setForm({ title: "", description: "", frequency: "daily", target_count: 1 });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/habits/${id}`); load(); };

  return (
    <div className="space-y-8 lg:mt-0 mt-10" data-testid="client-habits">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Daily ritual</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
            <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>Rituals</span>
          </h1>
          <div className="bronze-rule mt-6" />
        </div>
        <button className="btn-primary" onClick={() => setOpen(!open)} data-testid="new-habit-btn"><Plus size={14}/> New ritual</button>
      </header>

      {open && (
        <form onSubmit={add} className="card-luxe space-y-4 max-w-xl" data-testid="habit-form">
          <div><label className="label-luxe">Title</label><input required className="input-luxe" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="habit-title" /></div>
          <div><label className="label-luxe">Description</label><input className="input-luxe" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <button className="btn-primary" data-testid="habit-submit">Add</button>
        </form>
      )}

      <div className="space-y-3">
        {habits.map((h) => {
          const done = !!checkins[h.habit_id];
          return (
            <div key={h.habit_id} className="card-luxe flex items-center justify-between" data-testid={`habit-${h.habit_id}`}>
              <div className="flex items-center gap-4">
                <button onClick={() => toggle(h.habit_id, !done)}
                  className="w-7 h-7 border flex items-center justify-center transition-colors"
                  style={{ borderColor: done ? "var(--accent)" : "var(--border-strong)", background: done ? "var(--accent)" : "transparent" }}
                  data-testid={`habit-toggle-${h.habit_id}`}>
                  {done && <Check size={14} className="text-white" />}
                </button>
                <div>
                  <div className="font-medium">{h.title}</div>
                  <div className="text-xs text-secondary-luxe">{h.description}</div>
                </div>
              </div>
              <button className="btn-ghost" onClick={() => remove(h.habit_id)} data-testid={`habit-del-${h.habit_id}`}><Trash2 size={14}/></button>
            </div>
          );
        })}
        {habits.length === 0 && <div className="text-secondary-luxe">No habits yet — add your first ritual.</div>}
      </div>
    </div>
  );
}
